export const modal = {
  selector: '.v-component--modal',
  reactive: true,

  run(el, data = {}, root = document) {
    const trigger = el.querySelector('[data-modal-trigger]');
    const content = el.querySelector('[data-modal-content]');
    if (!content) {
      console.warn('Modal missing [data-modal-content]', el);
      return;
    }

    const closeButtons = content.querySelectorAll('[data-modal-close]');
    let overlay = null;
    let open = false;
    let lastFocusedElement = null;

    // Parse options from data-modal
    let modalOptions = {};
    try {
      const attr = el.getAttribute('data-modal');
      if (attr) modalOptions = JSON.parse(attr);
    } catch (e) {
      console.warn('Invalid JSON in data-modal attribute', e);
    }

    const config = {
      openOnClick: el.dataset.openOnClick === 'true',
      closeOnOutsideClick: el.dataset.closeOnOutsideClick !== 'false',
      hasOverlay: el.dataset.hasOverlay !== 'false',
      closeOnEsc: el.dataset.closeOnEsc !== 'false',
      awaitOpenAnimation: el.dataset.awaitOpenAnimation === 'true',
      awaitCloseAnimation: el.dataset.awaitCloseAnimation === 'true',
      disableScroll: el.dataset.disableScroll !== 'false',
      ...modalOptions
    };

    applyModalAccessibility(trigger, content);

const focusFirstElement = () => {
  // Focus the modal container first
  content.focus();

  // Optional fallback if modal is not focusable (shouldn’t happen if tabindex="-1" is set)
  if (document.activeElement !== content) {
    const focusable = content.querySelectorAll(
      'a[href], area[href], input:not([disabled]), select:not([disabled]), ' +
      'textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length > 0) {
      focusable[0].focus();
    }
  }
};


    const trapFocus = (e) => {
      if (!open) return;
      if (e.key !== 'Tab') return;

      const focusable = content.querySelectorAll(
        'a[href], area[href], input:not([disabled]), select:not([disabled]), ' +
        'textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    const showModal = () => {
      if (open) return;
      open = true;
      lastFocusedElement = document.activeElement;

      if (config.disableScroll) {
        document.body.style.overflow = 'hidden';
      }

      content.style.display = 'block';
      content.setAttribute('aria-hidden', 'false');
      el.classList.add('is-open');

      if (config.hasOverlay) createOverlay();

      if (config.awaitOpenAnimation) {
        content.classList.add('modal-opening');
        requestAnimationFrame(() => {
          content.classList.remove('modal-opening');
          focusFirstElement();
        });
      } else {
        focusFirstElement();
      }
    };

    const hideModal = () => {
      if (!open) return;
      open = false;

      if (config.awaitCloseAnimation) {
        content.classList.add('modal-closing');
        setTimeout(() => {
          content.classList.remove('modal-closing');
          closeCleanup();
        }, 0);
      } else {
        closeCleanup();
      }
    };

    const closeCleanup = () => {
      content.style.display = 'none';
      content.setAttribute('aria-hidden', 'true');
      el.classList.remove('is-open');

      if (config.disableScroll) {
        document.body.style.overflow = '';
      }

      if (overlay) removeOverlay();

      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus();
      }
    };

    const createOverlay = () => {
      overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      Object.assign(overlay.style, {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 999,
        background: 'rgba(0,0,0,0.4)'
      });
      overlay.addEventListener('click', () => {
        if (config.closeOnOutsideClick) hideModal();
      });
      document.body.appendChild(overlay);
    };

    const removeOverlay = () => {
      overlay?.remove();
      overlay = null;
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape' && config.closeOnEsc) {
        hideModal();
      }
    };

    // Bindings
    if (config.openOnClick && trigger) {
      trigger.addEventListener('click', showModal);
    }

    closeButtons.forEach((btn) => {
      btn.addEventListener('click', hideModal);
    });

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', trapFocus);

    // Cleanup if removed from DOM
    const observer = new MutationObserver(() => {
      if (!document.body.contains(el)) {
        cleanup();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const cleanup = () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', trapFocus);
      removeOverlay();
      observer.disconnect();
    };

    // Programmatic access (optional)
    el._modal = {
      show: showModal,
      hide: hideModal,
      destroy: cleanup
    };
  }
};

// Accessibility setup
function applyModalAccessibility(trigger, content) {
  if (!content) return;
  const modalId = content.id || `modal-${Math.random().toString(36).slice(2, 9)}`;
  content.id = modalId;

  trigger?.setAttribute('aria-haspopup', 'dialog');
  trigger?.setAttribute('aria-controls', modalId);
  trigger?.setAttribute('aria-expanded', 'false');

  content.setAttribute('role', 'dialog');
  content.setAttribute('aria-modal', 'true');
  content.setAttribute('aria-hidden', 'true');
  content.setAttribute('tabindex', '-1');
}
